import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import { useEffect, useState } from "react";
import {
  getSetpoints,
  requestClearFault,
  requestSetpoints,
  WELLPRO_REGISTERS,
} from "../../utils/controlConfig";
import { ProgressDialog } from "../plungerPage/progressPopup";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export const WellProMainCompressorCard = ({ unitId, data, fetchData }) => {
  const [form, setForm] = useState({
    loadStepControl: "2",
    speedControl: "2",
  });
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");
  const [progressMessage, setProgressMessage] = useState(
      "Check Device Response. Operation in-progress ...",
    );
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [pendingCmd, setPendingCmd] = useState(null);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const fetchWellProMainCompressor = async () => {
    if (!unitId) return;
    try {
      if (data) {
        setForm({
          loadStepControl: data._40322 ?? "2",
          speedControl: data._40298 ?? "2",
        });
      }
    } catch (err) {
      console.error("Failed to get wellPro main compressor:", err);
    }
  };

  useEffect(() => {
    if (unitId) {
      fetchWellProMainCompressor();
    }
  }, []);

  const validateFields = () => {
    const emptyFields = Object.entries(form).filter(
      ([key, value]) => value === "" || value === null || value === undefined,
    );
    return emptyFields.map(([key]) => key);
  };

  const updateWellProMainCompressor = async () => {
    const missingFields = validateFields();
    if (missingFields.length > 0) {
      toast.error(
        `Please fill in the following fields: ${missingFields.join(", ")}`,
        {
          autoClose: 5000,
        },
      );
      return;
    }
    try {
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        WELLPRO_REGISTERS,
      );
      const baseline = {
        time: Number(initialResp.data.data?.time || 0),
        response: initialResp.data.data?.response || {},
      };
      console.log("Initial baseline time", baseline);
      const sp = (label) => parseFloat(form[label]) || 0;
      let cmd = "S";
      cmd += `,${321},${sp("loadStepControl")}`;
      cmd += `,${297},${sp("speedControl")}`;
      setProgressOpen(true);
      setProgressSuccess(false);
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/cmd`,
        { unitId, cmd,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`, },
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Sending command:", cmd);
      setTimeout(() => {
        requestSetpoints(
          unitId,
          "P,321,1,297,1",
          baseline,
          setProgressSuccess,
          setProgressError,
          setProgressOpen,
        );
      }, 100);
    } catch (error) {
      console.log("Error to update main compressor PID controls", error);
    }
  };

  const handlePinLogin = async () => {
      const correctPin = "0720";
      let cmd = pendingCmd;
      if (pin !== correctPin) {
        setPinError("Invalid PIN. Please try again.");
        return;
      }
      try {
        // ✅ Close dialog and reset pin field
        setPinDialogOpen(false);
        setPin("");
  
        // Step 1: Start operation - show first message
        setProgressOpen(true);
        setProgressError(""); // clear any previous error
        setProgressSuccess(false);
        setProgressMessage("Controller Commands Execution Ongoing ...");
  
        // Step 2: Send command
        const response = await axios.post(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/cmd`,
          { unitId, cmd,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`, },
          {
            headers: {
              id: "anything",
              "Content-Type": "application/json",
            },
          },
        );
        console.log("Sending command:", cmd);
        // Step 3: If successful — show timed messages
        let pCmd = cmd.replace("S", "P");
        pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
        if (response.data.status === "OK") {
          // after 10 seconds → update message
          setTimeout(() => {
            setProgressMessage("Polling Device. Operation in progress ...");
            requestClearFault(
              unitId,
              pCmd,
              setProgressSuccess,
              setProgressError,
              setProgressOpen,
            );
          }, 100);
        } else {
          // If not OK — show error message
          setProgressError("No data found in this Device");
          setProgressOpen(true);
          setProgressSuccess(false);
        }
      } catch (error) {
        setProgressError("No data found in this Device");
        setProgressOpen(true);
        setProgressSuccess(false);
      }
    };

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
      <Paper sx={{ p: 2, bgcolor: "#1e293b", borderRadius: 2, mb: 2 }}>
        <Typography sx={{ mb: 2, fontSize: 14 }}>
          Main Compressor PID Controls
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
            fullWidth
            onClick={() =>
              getSetpoints(
                unitId,
                "P,321,1,297,1",
                setProgressSuccess,
                setProgressError,
                setProgressOpen,
              )
            }
          >
            Get Setpoints
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
            fullWidth
            onClick={updateWellProMainCompressor}
          >
            Submit
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13 }}>Load Step Control</Typography>
              <Select
                fullWidth
                size="small"
                value={form.loadStepControl ?? ""}
                onChange={(e) =>
                  handleChange("loadStepControl", e.target.value)
                }
                style={{ fontSize: 13, backgroundColor: "#1f2937" }}
              >
                <MenuItem disabled value="2" style={{ fontSize: 13 }}>
                  Select Option
                </MenuItem>
                <MenuItem value="1" style={{ fontSize: 13 }}>
                  Auto
                </MenuItem>
                <MenuItem value="0" style={{ fontSize: 13 }}>
                  Manual
                </MenuItem>
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13 }}>Speed Control</Typography>
              <Select
                fullWidth
                size="small"
                value={form.speedControl ?? ""}
                onChange={(e) => handleChange("speedControl", e.target.value)}
                sx={{ mb: 1 }}
                style={{ fontSize: 13, backgroundColor: "#1f2937" }}
              >
                <MenuItem disabled value="2" style={{ fontSize: 13 }}>
                  Select Option
                </MenuItem>
                <MenuItem value="1" style={{ fontSize: 13 }}>
                  Auto
                </MenuItem>
                <MenuItem value="0" style={{ fontSize: 13 }}>
                  Manual
                </MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, bgcolor: "#1e293b", borderRadius: 2 }}>
        <Typography sx={{ mb: 2, fontSize: 14 }}>Engine Controls</Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ fontSize: 12, mb: 2 }}
          onClick={() => {
                setPendingCmd("S,93,0,93,1,93,0,93,1");
                setPinDialogOpen(true);
              }}
        >
          Clear Fault
        </Button>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ fontSize: 12 }}
          onClick={() => {
              setPendingCmd("S,92,0,92,1");
              setPinDialogOpen(true);
            }}
        >
          Stop Engine
        </Button>
      </Paper>
      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await fetchData();
        }}
        loadingMessage={progressMessage}
        successMessage="Successfully updated!"
      />
      {/* 🔒 PIN Dialog */}
      <Dialog
        open={pinDialogOpen}
        onClose={() => setPinDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2, bgcolor: "#1e293b" } }}
      >
        <DialogTitle sx={{ fontSize: 16, color: "#fff" }}>
          Enter Access PIN
        </DialogTitle>
        <DialogContent>
          <TextField
            label="PIN"
            type="password"
            variant="outlined"
            fullWidth
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              setPinError("");
            }}
            sx={{
              input: { color: "#fff" },
              label: { color: "#ccc" },
              mt: 1,
              mb: 1,
            }}
          />
          {pinError && (
            <Typography sx={{ color: "white", fontSize: 13 }}>
              {pinError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setPinDialogOpen(false);
              setPin("");
              setPinError("");
            }}
            color="white"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={handlePinLogin} color="primary" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
